import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { api, formatBRL } from "../lib/api.js";
import {
  logApiError,
  stripeCheckoutLoadErrorDetail,
  stripeCheckoutLoadErrorMessage,
} from "../lib/apiErrors.js";
import { StripeEmbeddedCheckout } from "./StripeEmbeddedCheckout.js";
import {
  loadStripeCheckoutSession,
  saveStripeCheckoutSession,
} from "../lib/stripeCheckout.js";
import type { BillingInterval, Plan } from "../../../shared/types.js";

type OrderSnapshot = {
  id: string;
  status: string;
  plan?: Plan;
  interval?: BillingInterval;
  final_cents?: number;
  payment_method?: string;
};

export type IntegratedStripePanelProps = {
  orderId: string;
  plan: Plan;
  interval: BillingInterval;
  /** From change response when available — skips an extra fetch. */
  initialClientSecret?: string | null;
  initialPublishableKey?: string | null;
  initialFinalCents?: number | null;
  onBack: () => void;
  onPaid: (order: OrderSnapshot) => void;
};

/**
 * Inline Stripe Embedded Checkout for /integrated — keeps the customer in the
 * billing sheet instead of opening /stripe/checkout in a popup or top window.
 */
export function IntegratedStripePanel({
  orderId,
  plan,
  interval,
  initialClientSecret,
  initialPublishableKey,
  initialFinalCents,
  onBack,
  onPaid,
}: IntegratedStripePanelProps) {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState(initialClientSecret ?? "");
  const [publishableKey, setPublishableKey] = useState(initialPublishableKey ?? "");
  const [finalCents, setFinalCents] = useState(initialFinalCents ?? 0);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [paid, setPaid] = useState(false);
  const paymentHandledRef = useRef(false);

  const onPaymentIdentified = useCallback(
    (order: OrderSnapshot) => {
      if (paymentHandledRef.current) return;
      paymentHandledRef.current = true;
      setPaid(true);
      onPaid(order);
    },
    [onPaid],
  );

  const pollOrderStatus = useCallback(async () => {
    if (paymentHandledRef.current) return;
    try {
      await api(`/api/public/orders/${orderId}/recheck`, { method: "POST" });
      const r = await api<OrderSnapshot>(`/api/public/orders/${orderId}`);
      if (r.status === "paid") onPaymentIdentified(r);
    } catch {
      /* keep polling */
    }
  }, [orderId, onPaymentIdentified]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      setLoadError(null);

      const cached = loadStripeCheckoutSession(orderId);
      if (
        (initialClientSecret && initialPublishableKey) ||
        (cached?.clientSecret && cached.publishableKey)
      ) {
        const secret = initialClientSecret || cached!.clientSecret;
        const key = initialPublishableKey || cached!.publishableKey;
        const cents = initialFinalCents ?? cached?.finalCents ?? 0;
        if (!cancelled) {
          setClientSecret(secret);
          setPublishableKey(key);
          setFinalCents(cents);
          setLoading(false);
        }
        return;
      }

      try {
        const data = await api<{
          order_id: string;
          client_secret: string;
          publishable_key: string | null;
          final_cents: number;
        }>(`/api/public/orders/${orderId}/stripe-checkout`);
        if (cancelled) return;
        if (!data.publishable_key || !data.client_secret) {
          setError("stripe_not_configured");
          return;
        }
        saveStripeCheckoutSession(orderId, {
          clientSecret: data.client_secret,
          publishableKey: data.publishable_key,
          finalCents: data.final_cents,
        });
        setClientSecret(data.client_secret);
        setPublishableKey(data.publishable_key);
        setFinalCents(data.final_cents);
      } catch (err) {
        logApiError(`integrated-stripe.load orderId=${orderId}`, err);
        if (!cancelled) {
          setLoadError(err);
          setError("load_failed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId, initialClientSecret, initialPublishableKey, initialFinalCents]);

  useEffect(() => {
    if (paymentHandledRef.current) return;
    const handle = setInterval(() => void pollOrderStatus(), 10000);
    return () => clearInterval(handle);
  }, [pollOrderStatus]);

  if (paid) {
    return (
      <div className="card-glass p-6 lg:p-8 text-center text-[color:var(--text-primary)]">
        <div className="text-lg font-semibold">{t("paymentIdentified.title")}</div>
        <p className="text-sm text-ink-2 mt-2">{t("stripeCheckout.complete")}</p>
      </div>
    );
  }

  return (
    <div className="card-glass p-6 lg:p-8 text-[color:var(--text-primary)]">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-[color:var(--text-primary)] mb-4"
        onClick={onBack}
      >
        <ArrowLeft className="w-4 h-4" aria-hidden />
        {t("integrated.manageTitle")}
      </button>

      <h1 className="text-xl font-semibold" style={{ letterSpacing: "-0.02em" }}>
        {t("stripeCheckout.title")}
      </h1>
      <p className="text-sm text-ink-2 mt-1">
        {t(`plans.${plan}`)} · {t(`plans.${interval}`)}
        {finalCents > 0 ? ` · ${formatBRL(finalCents)}` : ""}
      </p>
      {finalCents > 0 && (
        <p className="text-sm text-ink-2 mt-1">
          {t("stripeCheckout.amount", { amount: formatBRL(finalCents) })}
        </p>
      )}
      <p className="text-xs text-ink-2 mt-2 mb-4">{t("stripeCheckout.walletsHint")}</p>

      {error && (
        <div className="text-sm text-danger py-8 text-center space-y-2">
          <p>
            {error === "stripe_not_configured"
              ? t("stripeCheckout.notConfigured")
              : error === "load_failed"
                ? stripeCheckoutLoadErrorMessage(loadError, t)
                : t("common.error")}
          </p>
          {import.meta.env.DEV && loadError != null && (
            <p className="text-xs text-ink-2 font-mono break-all">
              {stripeCheckoutLoadErrorDetail(loadError) ??
                (loadError instanceof Error ? loadError.message : String(loadError))}
            </p>
          )}
        </div>
      )}

      {loading && !error && (
        <div className="text-sm text-ink-2 py-12 text-center">{t("common.loading")}</div>
      )}

      {!loading && !error && publishableKey && clientSecret && (
        <div className="stripe-embedded-shell rounded-xl overflow-hidden bg-white min-h-[420px]">
          <StripeEmbeddedCheckout
            publishableKey={publishableKey}
            clientSecret={clientSecret}
            onComplete={() => void pollOrderStatus()}
          />
        </div>
      )}
    </div>
  );
}
